import { useState, type JSX } from 'react'
import { Sidebar } from 'primereact/sidebar'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { Button } from 'primereact/button'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'

const DEFAULT_SUBJECT = 'Test Email'
const DEFAULT_MESSAGE =
  'This is a test email sent to verify the email configuration.\nIf you have received this email, your email configuration is working successfully.'

interface TestResult {
  success: boolean
  message: string
}

export interface TestEmailDrawerProps {
  visible: boolean
  onHide: () => void
  /** 'smtp' shows recipient/subject/message and sends a real test email. 'imap' only attempts a connection/login. */
  mode: 'smtp' | 'imap'
  /** Reads the parent form's current (possibly unsaved) values at click time. */
  getDraftConfig: () => Record<string, unknown>
  testEndpoint: string
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

export default function TestEmailDrawer({
  visible,
  onHide,
  mode,
  getDraftConfig,
  testEndpoint,
}: TestEmailDrawerProps): JSX.Element {
  const toast = useToast()
  const [to, setTo] = useState('')
  const [subject, setSubject] = useState(DEFAULT_SUBJECT)
  const [message, setMessage] = useState(DEFAULT_MESSAGE)
  const [toError, setToError] = useState('')
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<TestResult | null>(null)

  const reset = (): void => {
    setTo('')
    setSubject(DEFAULT_SUBJECT)
    setMessage(DEFAULT_MESSAGE)
    setToError('')
    setResult(null)
  }

  const handleHide = (): void => {
    if (running) return
    reset()
    onHide()
  }

  const handleRun = async (): Promise<void> => {
    if (mode === 'smtp') {
      if (!to.trim()) {
        setToError('Recipient email is required')
        return
      }
      if (!isValidEmail(to.trim())) {
        setToError('Invalid email format')
        return
      }
    }
    setToError('')
    setRunning(true)
    setResult(null)
    try {
      const body =
        mode === 'smtp'
          ? { ...getDraftConfig(), to: to.trim(), subject: subject.trim(), message }
          : getDraftConfig()
      const response = await api.post<TestResult>(testEndpoint, body)
      setResult(response)
      if (response.success) {
        toast.success(response.message)
      } else {
        toast.error(response.message)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Test failed unexpectedly'
      setResult({ success: false, message })
      toast.error(message)
    } finally {
      setRunning(false)
    }
  }

  const title = mode === 'smtp' ? 'Test Email' : 'Test Connection'

  return (
    <Sidebar visible={visible} onHide={handleHide} position="right" className="w-full sm:w-[28rem]" header={title}>
      <div className="flex flex-col gap-4 p-1">
        {mode === 'smtp' ? (
          <>
            <div>
              <label htmlFor="test-email-to" className="mb-1 block text-sm font-medium text-gray-700">
                Send Test Email To <span className="text-red-500">*</span>
              </label>
              <InputText
                id="test-email-to"
                value={to}
                onChange={(e) => { setTo(e.target.value); setToError('') }}
                className={`w-full ${toError ? 'p-invalid' : ''}`}
                placeholder="recipient@example.com"
                type="email"
                disabled={running}
              />
              {toError && <p className="mt-1 text-xs text-red-600">{toError}</p>}
            </div>

            <div>
              <label htmlFor="test-email-subject" className="mb-1 block text-sm font-medium text-gray-700">
                Subject
              </label>
              <InputText
                id="test-email-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full"
                disabled={running}
              />
            </div>

            <div>
              <label htmlFor="test-email-message" className="mb-1 block text-sm font-medium text-gray-700">
                Message
              </label>
              <InputTextarea
                id="test-email-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                className="w-full"
                disabled={running}
              />
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-600">
            This will attempt to connect and log in to the mail server using the values currently entered in this form.
          </p>
        )}

        {result && (
          <div
            className={`rounded-lg border p-3 text-sm ${
              result.success
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
          >
            {result.message}
          </div>
        )}

        <div className="mt-2 flex gap-3">
          <Button
            type="button"
            label="Cancel"
            severity="secondary"
            outlined
            onClick={handleHide}
            disabled={running}
          />
          <Button
            type="button"
            label={running ? 'Sending…' : mode === 'smtp' ? 'Send Test Email' : 'Run Test'}
            icon={running ? 'pi pi-spinner pi-spin' : undefined}
            onClick={() => { void handleRun() }}
            disabled={running}
          />
        </div>
      </div>
    </Sidebar>
  )
}
